/**
 * DashboardHome — landing for the admin console.
 *
 * Aggregate, real-data overview composed from endpoints that already exist:
 *   - dossier list (document-svc)  → open / awaiting-documents counts + recent
 *   - platform costs (admin-svc)   → month-to-date platform spend (super_admin
 *                                    only; brokerage_admin gets 403 → hidden)
 *
 * Cards link through to the wired feature screens. This replaces the Phase-0
 * static scaffold.
 */

import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import { billingService, dossierService } from "@re-platform/api-client";
import type { DossierSummary } from "@re-platform/types";
import { Card, StatusPill } from "@re-platform/ui";

import { api } from "../lib/api";
import { Avatar, cardList, cardShell, Chevron } from "../lib/cards";
import { TenantSpendChart } from "./TenantSpendChart";
import { brokerageLabel, useMergedQuery } from "../lib/mergedQuery";
import { useMemberships } from "../lib/memberships";
import { useRoles } from "../lib/roles";

const OPEN_STATUSES = new Set(["PENDING", "IN_PROGRESS"]);

function isoDate(d: Date): string {
  return d.toISOString().slice(0, 10);
}

function fmtUsd(v: string | null | undefined): string {
  if (v === null || v === undefined) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

export function DashboardHome() {
  const { isSuperAdmin, isAgent, unknown } = useRoles();
  // Platform spend is super_admin-only (the billing routes 403 everyone else),
  // so only super_admin (or an unknown-role dev session) gets the card + query.
  const showBilling = isSuperAdmin || unknown;

  // Dual-licensed staff see BOTH brokerages at once (PR 4). One human with one
  // login holds a users row per brokerage, and every list endpoint is
  // tenant-scoped — so "how is my book doing?" used to mean toggling back and
  // forth and adding up in your head. Single-brokerage staff take the plain
  // path: no fan-out, no header, request unchanged.
  const memberships = useMemberships();
  const rows = memberships.data ?? [];
  const dossiersQuery = useMergedQuery<DossierSummary>(
    rows,
    ["dossiers"],
    (client) => dossierService.list(client, { limit: 200 }),
    { enabled: rows.length > 0 },
  );

  // Month-to-date platform spend. super_admin-only — a brokerage_admin token
  // 403s, so don't retry and surface the card as unavailable instead.
  const monthStart = isoDate(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  );
  // "Since Jul 1", not "Since 2026-07-01" — a tile is read at a glance.
  const monthLabel = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
  ).toLocaleDateString(undefined, { month: "short", day: "numeric" });
  const costsQuery = useQuery({
    queryKey: ["billing", "costs", "platform", "mtd", monthStart],
    queryFn: () => billingService.getPlatformCosts(api, { since: monthStart }),
    enabled: showBilling,
    retry: false,
    staleTime: 60_000,
  });

  // Everyone else's month-to-date spend comes off agent-costs: a
  // brokerage_admin gets their brokerage's total, an agent their own row (the
  // endpoint pins an agent caller to themselves). super_admin has no single
  // tenant to ask about, so they keep the platform figure above.
  const ownCostsQuery = useQuery({
    queryKey: ["billing", "agent-costs", "mtd", monthStart],
    queryFn: () => billingService.getAgentCosts(api, { since: monthStart }),
    enabled: !showBilling,
    retry: false,
    staleTime: 60_000,
  });

  // The three queues an agent actually works from. Merged across brokerages
  // like everything else, since a dual-licensed agent has two of each.
  const reviewQueue = useMergedQuery<{ id: string }>(
    rows,
    ["review-queue"],
    (client) => client.request<{ id: string }[]>("/document/review-queue"),
    { enabled: isAgent && rows.length > 0 },
  );
  const showings = useMergedQuery<{ id: string; status: string }>(
    rows,
    ["appointments"],
    (client) =>
      client.request<{ id: string; status: string }[]>("/appointment/requests"),
    { enabled: isAgent && rows.length > 0 },
  );
  const followups = useMergedQuery<{ id: string }>(
    rows,
    ["followups", "NEW"],
    (client) =>
      client.request<{ id: string }[]>("/voice-agent/followups?status=NEW"),
    { enabled: isAgent && rows.length > 0 },
  );
  // Forms HELD for the agent to finish (gap I26). The whole packet waits behind
  // one of these — a listing agreement cannot send until the agent supplies
  // ¶2A's legal description — so the client is sitting looking at "your agent is
  // preparing your documents" until this is cleared. #146 put a card on the
  // Forms SCREEN, which only tells an agent who already went to Forms; the one
  // place that answers "what is waiting on me?" is this dashboard.
  const heldForms = useMergedQuery<{ id: string }>(
    rows,
    ["forms", "PREFILLED"],
    (client) =>
      client.request<{ id: string }[]>("/trec/forms?status=PREFILLED"),
    { enabled: isAgent && rows.length > 0 },
  );

  // A showing that still needs the agent to act. COMPLETED/CANCELLED ones are
  // history, and counting them would make the tile a number that only grows.
  const pendingShowings = showings.rows.filter((r) =>
    ["REQUESTED", "PENDING"].includes(r.row.status),
  );

  /** MTD spend for the caller, whichever endpoint answers for their role. */
  const spend = showBilling
    ? {
        pending: costsQuery.isPending,
        error: costsQuery.isError,
        value: costsQuery.data?.platform_total,
      }
    : {
        pending: ownCostsQuery.isPending,
        error: ownCostsQuery.isError,
        value: ownCostsQuery.data?.total_usage,
      };

  const scoped = dossiersQuery.rows;
  const open = scoped.filter((d) => OPEN_STATUSES.has(d.row.status));
  const awaiting = open.filter(
    (d) => d.row.slots_validated < d.row.slots_total,
  );
  // Merged from two tenants, so the per-tenant ordering no longer holds — sort
  // the combined set before taking the most recent six, or "recent" would mean
  // "the first brokerage's recent, then the second's".
  const recent = [...scoped]
    .sort(
      (a, b) =>
        new Date(b.row.created_at).getTime() -
        new Date(a.row.created_at).getTime(),
    )
    .slice(0, 6);

  // Copy adapts to scope: an agent sees only their assigned clients, a
  // brokerage_admin their brokerage, a super_admin the whole platform.
  const scopeBlurb = isAgent
    ? "Your assigned clients at a glance."
    : isSuperAdmin
      ? "Platform overview at a glance."
      : "Your brokerage at a glance.";
  const recentSubtitle = isAgent
    ? "Latest activity across your clients"
    : isSuperAdmin
      ? "Latest activity across tenants"
      : "Latest activity across your brokerage";

  return (
    <>
      <h1 style={{ marginTop: 0 }}>Admin dashboard</h1>
      <p style={{ color: "var(--rp-color-text-muted)", marginTop: 0 }}>
        {scopeBlurb}
      </p>

      {dossiersQuery.isError && (
        <Card padding="lg">
          <p style={{ margin: 0 }}>
            Couldn't load dossiers: {dossiersQuery.error?.message}. Is the
            session token set (sidebar) and staff-roled?
          </p>
        </Card>
      )}

      <div
        // auto-fit, not a fixed `repeat(2|3, 1fr)`: the count already varies
        // by role, and a fixed count cannot reflow — three stat cards were
        // squeezed to unreadable slivers on a narrow window rather than
        // wrapping onto a second line.
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 16,
          marginBottom: 24,
        }}
      >
        {/* An agent works from queues, not from a portfolio count: what they
            open the console to answer is "what is waiting on me?". A
            brokerage_admin / super_admin keeps the book-level view. */}
        {isAgent ? (
          <>
            <StatCard
              label="Document review"
              value={queueValue(reviewQueue, reviewQueue.rows.length)}
              to="/document-review"
              hint="Flagged by the classifier"
              attention={!reviewQueue.isError && reviewQueue.rows.length > 0}
            />
            <StatCard
              label="Showing requests"
              value={queueValue(showings, pendingShowings.length)}
              to="/appointments"
              hint="Awaiting your confirmation"
              attention={!showings.isError && pendingShowings.length > 0}
            />
            <StatCard
              label="Requests & questions"
              value={queueValue(followups, followups.rows.length)}
              to="/requests"
              hint="Unanswered"
              attention={!followups.isError && followups.rows.length > 0}
            />
            <StatCard
              label="Documents to send"
              value={queueValue(heldForms, heldForms.rows.length)}
              to="/forms"
              hint="Client is waiting on you"
              attention={!heldForms.isError && heldForms.rows.length > 0}
            />
          </>
        ) : (
          <>
            <StatCard
              label="Open dossiers"
              value={dossiersQuery.isPending ? "…" : String(open.length)}
              to="/dossiers/active"
              hint="Pending or in progress"
            />
            <StatCard
              label="Awaiting documents"
              value={dossiersQuery.isPending ? "…" : String(awaiting.length)}
              to="/dossiers/active"
              hint="Open dossiers with unvalidated slots"
              attention={awaiting.length > 0}
            />
          </>
        )}
        <StatCard
          label={showBilling ? "Platform spend · MTD" : "Spend · MTD"}
          value={spend.pending ? "…" : spend.error ? "—" : fmtUsd(spend.value)}
          to={
            isAgent
              ? "/my-usage"
              : showBilling
                ? "/billing/costs"
                : "/billing/agent-usage"
          }
          hint={spend.error ? "Unavailable" : `Since ${monthLabel}`}
        />
      </div>

      {/* The platform view leads with money per brokerage — a super_admin's
          question is "who are we billing and what are they costing us?", which
          a list of six recent dossiers across every tenant never answered.
          Everyone else keeps the recent list, which IS their working set. */}
      {showBilling ? (
        <TenantSpendChart />
      ) : (
        <Card title="Recent dossiers" subtitle={recentSubtitle}>
          {dossiersQuery.isPending && <p style={{ margin: 0 }}>Loading…</p>}
          {!dossiersQuery.isPending && recent.length === 0 && (
            <p style={{ margin: 0, color: "var(--rp-color-text-muted)" }}>
              No dossiers yet.
            </p>
          )}
          {recent.length > 0 && (
            <div style={cardList}>
              {recent.map(({ row: d, tenant }) => {
                const name = d.client_name ?? d.client_email ?? "—";
                const complete =
                  d.slots_total > 0 && d.slots_validated >= d.slots_total;
                return (
                  // Two brokerages can hold rows with the same id only by
                  // coincidence, but the key must be unique across the merged
                  // set regardless — so it carries the tenant too.
                  <Link
                    key={`${tenant.tenant_id}:${d.id}`}
                    to={`/clients/${d.client_id}`}
                    className="rp-clientcard"
                    style={{
                      ...cardShell,
                      textDecoration: "none",
                      color: "inherit",
                    }}
                  >
                    <Avatar name={name} size={34} />
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>
                        {name}
                      </div>
                      {/* Template, brokerage and date on one muted line: three
                        columns of one short value each in the table this
                        replaces. */}
                      <div
                        style={{
                          color: "var(--rp-color-text-muted)",
                          fontSize: 12.5,
                          marginTop: 2,
                        }}
                      >
                        {[
                          d.template_display_name ?? d.template_key,
                          dossiersQuery.isDual ? brokerageLabel(tenant) : null,
                          new Date(d.created_at).toLocaleDateString(),
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 12.5,
                        fontWeight: 600,
                        padding: "3px 10px",
                        borderRadius: 999,
                        whiteSpace: "nowrap",
                        background: complete
                          ? "var(--rp-color-success-faint)"
                          : "var(--rp-color-surface-2)",
                        color: complete
                          ? "var(--rp-color-success)"
                          : "var(--rp-color-text-muted)",
                      }}
                    >
                      {d.slots_validated}/{d.slots_total}
                    </span>
                    <StatusPill
                      tone={d.status === "IN_PROGRESS" ? "info" : "neutral"}
                    >
                      {d.status.toLowerCase().replace("_", " ")}
                    </StatusPill>
                    <Chevron />
                  </Link>
                );
              })}
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/**
 * A queue tile's number, given the query behind it.
 *
 * A queue that could not be reached must not read as an empty one. These three
 * tiles each render a count, and a failed fetch leaves `rows` empty — so an
 * agent whose review queue 404s was told, in the same type as a real answer,
 * that they have nothing waiting. `/document/review-queue` has no backend yet,
 * which made that the permanent reading rather than a transient one.
 */
function queueValue(
  query: { isPending: boolean; isError: boolean },
  count: number,
): string {
  if (query.isPending) return "…";
  if (query.isError) return "—";
  return String(count);
}

function StatCard({
  label,
  value,
  to,
  hint,
  attention,
}: {
  label: string;
  value: string;
  to: string;
  hint?: string;
  /** Amber value — something in here is waiting on someone. */
  attention?: boolean;
}) {
  return (
    <Link
      to={to}
      style={{
        textDecoration: "none",
        color: "inherit",
        border: "1px solid var(--rp-color-border)",
        borderRadius: "var(--rp-radius-lg)",
        background: "var(--rp-color-surface)",
        boxShadow: "var(--rp-shadow-sm)",
        padding: "16px 20px",
        display: "block",
      }}
    >
      <div
        style={{
          fontSize: 12,
          color: "var(--rp-color-text-muted)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 28,
          fontWeight: 700,
          color: attention ? "var(--rp-color-warning)" : undefined,
        }}
      >
        {value}
      </div>
      {hint && (
        <div
          style={{
            fontSize: 12,
            color: "var(--rp-color-text-muted)",
            marginTop: 4,
          }}
        >
          {hint}
        </div>
      )}
    </Link>
  );
}
